import { Camera, ShieldCheck } from 'lucide-react';
import Hotspot from './Hotspot';

const VERIFICATION_LABEL: Record<string, string> = {
  draft: 'Draft',
  family_confirmed: 'Witnessed by family',
  evidence_attached: 'Evidence added',
  verified: 'Institutionally confirmed',
};

const VERIFICATION_COLOR: Record<string, string> = {
  draft: '#94a3b8',
  family_confirmed: '#d9a441',
  evidence_attached: '#5fa8d3',
  verified: '#1d9e75',
};

const MEMBERS = [
  { name: 'Kwame Osei', relationship: 'You', years: 'b. 1985', status: 'verified' },
  { name: 'Joseph Osei', relationship: 'Father', years: 'b. 1952', status: 'family_confirmed' },
  {
    name: 'Grace Morrison-Osei',
    relationship: 'Mother',
    years: 'b. 1956',
    status: 'family_confirmed',
  },
  {
    name: 'Emmanuel Osei',
    relationship: 'Grandfather',
    years: '1921 - 1988',
    status: 'evidence_attached',
  },
] as const;

/** Fictional preview only -- the Osei-Morrison family archive is existing sample data used
 * throughout the family-ledger feature (see @/app/family-ledger/types SAMPLE_MEMBERS). */
export default function FamilyLedgerPreview() {
  return (
    <div className="relative min-w-0">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-900">
            The Osei-Morrison Family Archive
          </p>
          <p className="truncate text-xs text-slate-500">
            Kumasi &amp; Cape Coast, Ghana -&gt; London
          </p>
        </div>
        <Hotspot
          label="See family connection"
          note="Relationships are drawn between real people, so the tree shows how everyone actually connects."
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {MEMBERS.map((member) => (
          <div
            key={member.name}
            className="flex items-center gap-2.5 rounded-[9px] border border-slate-200 bg-white p-2.5"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#3a211c] text-xs font-bold text-[#ffb09c]">
              {member.name
                .split(' ')
                .map((part) => part[0])
                .slice(0, 2)
                .join('')}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-slate-900">{member.name}</p>
              <p className="truncate text-[11px] text-slate-500">
                {member.relationship} &middot; {member.years}
              </p>
            </div>
            <span
              className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold"
              style={{
                background: `${VERIFICATION_COLOR[member.status]}1f`,
                color: VERIFICATION_COLOR[member.status],
              }}
            >
              {VERIFICATION_LABEL[member.status]}
            </span>
          </div>
        ))}
      </div>

      <div className="relative mt-4 flex items-center gap-3 rounded-[9px] border border-[#e88a73]/30 bg-[#fdf3f0] p-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#3a211c] text-[#ffb09c]">
          <Camera size={15} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-bold text-slate-900">Emmanuel at the Kumasi market, 1965</p>
          <p className="truncate text-[11px] text-slate-500">
            Photograph &middot; evidence attached &middot; family_only
          </p>
        </div>
        <ShieldCheck size={15} className="ml-auto shrink-0 text-[#5fa8d3]" aria-hidden="true" />
        <Hotspot
          label="View evidence"
          note="Photos and documents attach directly to the person and the record they support."
          className="absolute -bottom-3 right-3"
        />
      </div>
    </div>
  );
}
